// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMantineTheme } from '@mantine/core';
import { Anchor, Badge, Box, Button, Card, Center, Checkbox, Container, PasswordInput, Stack, Text, TextInput, Title } from '@/components/ui';
import { IconMail, IconLock, IconArrowLeft, IconSparkles } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { SocialLoginButtons } from '../components';

export default function LoginGlassmorphism() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const theme = useMantineTheme();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Login attempt:', { email, password, rememberMe });
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        backgroundImage: 'url(https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
      }}
    >
      {/* Overlay with blur */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(8px)',
        }}
      />

      <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <SlideIn direction="up" delay={100}>
            {/* Glassmorphism Card */}
            <Box
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: theme.radius.lg,
                padding: '2.5rem',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
              }}
            >
              <Stack gap="lg">
                {/* Back button */}
                <Button
                  component={Link}
                  to="/demo/auth-pages/login"
                  variant="subtle"
                  size="xs"
                  leftSection={<IconArrowLeft size={14} />}
                  w="fit-content"
                  c="white"
                >
                  Back to variants
                </Button>

                <FadeIn delay={200}>
                  <Stack gap="xs" align="center">
                    <Badge
                      leftSection={<IconSparkles size={12} />}
                      variant="white"
                      size="lg"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                      }}
                    >
                      Glassmorphism
                    </Badge>
                    <Title order={2} ta="center" c="white" mt="md">
                      Welcome back
                    </Title>
                    <Text c="white" size="sm" ta="center" style={{ opacity: 0.9 }}>
                      Sign in to your account to continue
                    </Text>
                  </Stack>
                </FadeIn>

                <SlideIn direction="up" delay={300}>
                  <form onSubmit={handleSubmit}>
                    <Stack gap="md">
                      <TextInput
                        label="Email address"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        leftSection={<IconMail size={16} />}
                        required
                        styles={{
                          label: { color: 'white', fontWeight: 500 },
                          input: {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            '&::placeholder': { color: 'rgba(255, 255, 255, 0.6)' },
                            '&:focus': {
                              borderColor: 'rgba(255, 255, 255, 0.4)',
                            },
                          },
                        }}
                      />

                      <PasswordInput
                        label="Password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        styles={{
                          label: { color: 'white', fontWeight: 500 },
                          input: {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            '&::placeholder': { color: 'rgba(255, 255, 255, 0.6)' },
                            '&:focus': {
                              borderColor: 'rgba(255, 255, 255, 0.4)',
                            },
                          },
                          innerInput: { color: 'white' },
                        }}
                      />

                      <Checkbox
                        label="Remember me for 30 days"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.currentTarget.checked)}
                        styles={{
                          label: { color: 'white' },
                          input: {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                          },
                        }}
                      />

                      <Button
                        type="submit"
                        fullWidth
                        size="md"
                        variant="white"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        Sign in
                      </Button>
                    </Stack>
                  </form>
                </SlideIn>

                <FadeIn delay={400}>
                  <Stack gap="md">
                    <Text ta="center" size="sm" c="white" style={{ opacity: 0.9 }}>
                      Or continue with
                    </Text>
                    <SocialLoginButtons
                      providers={['google', 'github']}
                      layout="row"
                      variant="white"
                    />
                  </Stack>
                </FadeIn>

                <FadeIn delay={500}>
                  <Stack gap="xs" align="center">
                    <Anchor component={Link} to="/demo/auth-pages/forgot-password/glassmorphism" size="sm" c="white">
                      Forgot your password?
                    </Anchor>
                    <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                      Don't have an account?{' '}
                      <Anchor component={Link} to="/demo/auth-pages/register/glassmorphism" fw={500} c="white">
                        Sign up
                      </Anchor>
                    </Text>
                  </Stack>
                </FadeIn>
              </Stack>
            </Box>
          </SlideIn>
        </Container>
      </Center>
    </Box>
  );
}
